import * as React from "react";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { request } from "../../../../services/cpln";
import { getBillingErrorMessage } from "../../../../services/utils";
import { Button, notification } from "antd";
import { HUBSPOT_SERVICE_URL, IS_DEPLOYMENT_ENV_TEST, IS_HUBSPOT_INTEGRATION_ENABLED } from "../../../../envVariables";
import { getHubspotUtk } from "../../../account/create/useInformHubspot";
import type { StripeCardElementChangeEvent } from "@stripe/stripe-js";
import { get_CARD_ELEMENT_OPTIONS } from "../../../account/add";
import { useAppSelector } from "../../../../store/hooks";
import { selectIsCplnTeam } from "../../../../store/userSlice";
import { formSet, formSetValue, useCpFormAnyValue, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpCheckbox } from "../../../gvc/testNewForm/CpCheckbox";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { CpDescription } from "../../../gvc/testNewForm/CpDescription";
import { CpErrorWarning } from "../../../gvc/testNewForm/CpErrorWarning";

export const AccountCreatePaymentMethod: React.FC = ({}) => {
  const { id } = useCpFormContext();

  const isCplnTeam = useAppSelector(selectIsCplnTeam);
  const userEmail = useAppSelector((state) => state.user.email);
  const theme = useAppSelector((state) => state.theme.theme);

  const createdAccountId = useCpFormValue(`meta.createdAccount.id`);
  const hasGivenConsent = useCpFormValue(`meta.paymentMethod.consented`);
  const canCreatePaymentMethod = useCpFormValue(`meta.paymentMethod.canCreate`);
  const submitting = useCpFormAnyValue(`meta.submitting`);

  const stripe = useStripe();
  const elements = useElements();

  const [clientSecret, setClientSecret] = React.useState("");
  const [clientSecretError, setClientSecretError] = React.useState("");
  const [addError, setAddError] = React.useState("");
  const [cardError, setCardError] = React.useState<any>(undefined);
  const [cardComplete, setCardComplete] = React.useState(false);

  React.useEffect(() => {
    getClientSecret();
  }, []);

  React.useEffect(() => {
    let canCreate = true;
    if (!!addError) canCreate = false;
    if (!!clientSecretError) canCreate = false;
    if (!hasGivenConsent) canCreate = false;
    if (!!cardError) canCreate = false;
    if (!cardComplete) canCreate = false;

    formSetValue(id, `meta.paymentMethod.canCreate`, canCreate);
  }, [addError, clientSecretError, hasGivenConsent, cardError, cardComplete]);

  async function getClientSecret() {
    try {
      formSet(id, `meta.submitting`, true);
      setClientSecretError("");
      const { data } = await request({
        method: "post",
        service: "billing-ng",
        url: `/account/${createdAccountId}/payment-setup`,
      });
      setClientSecret(data.clientSecret);
    } catch (e) {
      setClientSecretError(getBillingErrorMessage(e));
      setClientSecret("");
    } finally {
      formSet(id, `meta.submitting`, false);
    }
  }

  function reset() {
    getClientSecret();
    setAddError("");
    setClientSecretError("");
  }

  async function onConfirm() {
    if (!stripe || !elements || !clientSecret || !!clientSecretError || !cardComplete || !!cardError) {
      return;
    }
    formSet(id, `meta.submitting`, true);

    try {
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement)!,
        },
      });

      if (result.error) {
        setAddError(result.error.message || "Could not finalize adding payment method");
      } else {
        await request({
          method: "put",
          service: "billing-ng",
          url: `/account/${createdAccountId}/default-payment/${result.setupIntent.payment_method}`,
        });
        notification.success({
          message: "Success",
          description: `Added payment method as default`,
        });

        if (IS_HUBSPOT_INTEGRATION_ENABLED && !isCplnTeam) {
          try {
            const hubspotutk = getHubspotUtk();

            await request({
              service: "self",
              method: "post",
              url: `${HUBSPOT_SERVICE_URL}/addpaymentmethod`,
              body: {
                email: userEmail,
                hubspotutk,
                environment: IS_DEPLOYMENT_ENV_TEST ? "test" : "production",
              },
            });
          } catch (e) {
            console.error(
              "Failed to set has payment method in hubspot",
              e.message,
              JSON.stringify(e.response?.data || {}),
            );
          }
        }
      }
      formSetValue(id, "meta.formStep", "orgGvc");
    } catch (e) {
      setAddError(getBillingErrorMessage(e));
    } finally {
      formSet(id, "meta.submitting", false);
    }
  }

  function onCardChange(e: StripeCardElementChangeEvent) {
    setCardComplete(e.complete);
    setCardError(e.error);
  }

  return (
    <div className="account-create-payment">
      <div className="mr-4 mb-4 font-semibold">Card Information</div>
      {clientSecretError ? (
        <div>
          <div className="color-danger my-2 py-2 rounded">{clientSecretError}</div>
          <Button htmlType={`button`} type={"primary"} onClick={reset}>
            Try Again
          </Button>
        </div>
      ) : addError ? (
        <div>
          <div className="color-danger my-2 rounded">An error has occurred processing your payment method:</div>
          <div className="color-danger my-2 rounded">{addError}</div>
          <Button htmlType={`button`} type={"primary"} onClick={reset}>
            Try Again
          </Button>
        </div>
      ) : (
        <>
          <div className="cc border px-4 py-2">
            <CardElement options={get_CARD_ELEMENT_OPTIONS(theme === "dark")} onChange={onCardChange} />
          </div>
          <CpFormItem>
            <div className="flex items-center gap-2">
              <CpCheckbox name={`meta.paymentMethod.consented`} />
              <CpLabel name={`meta.paymentMethod.consented`}>Approve</CpLabel>
            </div>
            <CpDescription>
              I agree that by providing my credit card information, I authorize Control Plane Corporation to charge my
              credit card for the use of services provided by Control Plane Platform. I acknowledge and agree that
              charges will apply, based on actual usage, and that I am responsible for all applicable charges until I
              cancel my account or stop running services on the Control Plane Platform.
            </CpDescription>
            <CpErrorWarning name="meta.paymentMethod.consented" />
          </CpFormItem>
          {IS_DEPLOYMENT_ENV_TEST ? (
            <div className="text-sm mt-12">
              <div className="mt-2">Stripe Test Cards</div>
              <div className="mt-2">Europe 3D Secure: 4000 0025 0000 3155</div>
              <div className="mt-2">America: 4242 4242 4242 4242</div>
              <div className="mt-2">Failing CVC: 4000 0000 0000 0101</div>
            </div>
          ) : null}
          <Button
            className="mt-4 action"
            type="primary"
            block
            onClick={() => onConfirm()}
            loading={submitting}
            disabled={!canCreatePaymentMethod || submitting}
          >
            Add & Continue
          </Button>
        </>
      )}
    </div>
  );
};
